import { logger } from "../utils/logger";
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Typography, CircularProgress, Alert } from '@mui/material';
import { useSnackbar } from 'notistack';
import { api } from '../services/api';
import platformService from '../services/platformService';
import { useAuth } from '../contexts/AuthContext';
import { mapErrorToUserMessage } from '../utils/errorMapping';
import { importShopifyTransactions } from '../utils/shopifyImport';

const ShopifyCallback: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const [error, setError] = useState<string | null>(null);
    const [importing, setImporting] = useState(false);
    const [importProgress, setImportProgress] = useState<string>('');
    const { user, refreshUser } = useAuth();
    const hasPostedRef = useRef(false); // Prevent duplicate POSTs across StrictMode/double-mount
    const [loginPrompt, setLoginPrompt] = useState(false);
    const [restoring, setRestoring] = useState(false);
    const [integrationContext, setIntegrationContext] = useState<{ integrationId: number; companyId: number } | null>(null);
    const [syncResult, setSyncResult] = useState<any>(null);

    useEffect(() => {
        const handleCallback = async () => {
            // Prevent duplicate POSTs due to StrictMode/double-mount
            if (hasPostedRef.current) return;
            
            logger.log('[Shopify Callback] Starting callback handling');
            try {
                const code = searchParams.get('code');
                const state = searchParams.get('state');
                const shop = searchParams.get('shop');
                const error = searchParams.get('error');
                const errorDescription = searchParams.get('error_description');

                logger.log('[Shopify Callback] URL params:', { code: code?.substring(0, 20) + '...', state, shop, error, errorDescription });

                // Check for OAuth errors first
                if (error) {
                    setError(`Shopify OAuth error: ${error}${errorDescription ? ` - ${errorDescription}` : ''}`);
                    return;
                }

                if (!code) {
                    logger.error('[Shopify Callback] No authorization code found in URL');
                    logger.error('[Shopify Callback] This usually means the user accessed the callback page directly');
                    setError('Missing authorization code. This page should only be accessed through the Shopify OAuth flow. Please go back to the import page and try connecting to Shopify again.');
                    
                    // Redirect to integrations page after a delay
                    setTimeout(() => {
                        navigate('/platforms/integrations');
                    }, 5000);
                    return;
                }
                
                // For Shopify, shop parameter is required, but state can be optional in some flows
                if (!shop) {
                    logger.error('[Shopify Callback] No shop parameter found in URL');
                    setError('Missing shop parameter. Please try connecting again.');
                    return;
                }

                // Mark as posted just before making the request
                hasPostedRef.current = true;

                // Send the authorization code to the backend
                const response = await platformService.shopifyOAuthCallback(code, state, shop, Object.fromEntries(searchParams));

                if (response.success) {
                    enqueueSnackbar('Shopify connected successfully!', { variant: 'success' });
                    window.history.replaceState({}, document.title, window.location.pathname);
                    if (response.integration_id && response.company_id) {
                        setIntegrationContext({ integrationId: Number(response.integration_id), companyId: Number(response.company_id) });
                    }
                    if (response.sync) {
                        setSyncResult(response.sync);
                    }
                    
                    // Always try to refresh user session first after OAuth success
                    logger.log('[Shopify Callback] OAuth successful, refreshing user session...');
                    setRestoring(true);
                    
                    try {
                        await refreshUser();
                        logger.log('[Shopify Callback] User session refreshed, user:', user);
                        
                        // Check if we have a valid user after refresh
                        const effectiveCompanyId = response.primary_company_id || user?.primary_company_id;
                        if (effectiveCompanyId) {
                            logger.log('[Shopify Callback] User has company_id, starting import...');
                            setRestoring(false);
                            setImporting(true);
                            setImportProgress('Starting Shopify import...');
                            
                            await importShopifyTransactions({
                                companyId: effectiveCompanyId,
                                integrationId: response.integration_id,
                                setImporting,
                                setUploadError: setError,
                                onProgress: setImportProgress,
                                onComplete: (success) => {
                                    setImporting(false);
                                    if (success) {
                                        enqueueSnackbar('Shopify import complete!', { variant: 'success' });
                                        setTimeout(() => navigate('/platforms/integrations'), 2000);
                                    } else {
                                        setError('Shopify import failed.');
                                    }
                                }
                            });
                        } else {
                            logger.log('[Shopify Callback] No user or company_id after refresh, showing login prompt');
                            setRestoring(false);
                            setLoginPrompt(true);
                        }
                    } catch (refreshError) {
                        logger.error('[Shopify Callback] Error refreshing user session:', refreshError);
                        setRestoring(false);
                        setLoginPrompt(true);
                    }
                } else {
                    setError('Failed to complete Shopify connection');
                }
            } catch (err: any) {
                logger.error('Shopify callback error:', err);
                setError(mapErrorToUserMessage(err));
            }
        };

        handleCallback();
    }, [searchParams, navigate, enqueueSnackbar, user, refreshUser]);

    if (loginPrompt) {
        return (
            <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                minHeight="50vh"
                p={3}
            >
                <Alert severity="success" sx={{ mb: 2 }}>
                    Shopify connected! Please restore your session to continue.
                </Alert>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    Your Shopify account is now connected. Click below to restore your session and continue importing your transactions.
                </Typography>
                <Box sx={{ mb: 2 }}>
                    <button
                        onClick={async () => {
                            logger.log('[Shopify Callback] Manual restore session clicked');
                            setRestoring(true);
                            setError(null);
                            
                            try {
                                await refreshUser();
                                logger.log('[Shopify Callback] Manual session refresh completed, user:', user);
                                
                                const effectiveCompanyId = integrationContext?.companyId || user?.primary_company_id;
                                if (effectiveCompanyId) {
                                    logger.log('[Shopify Callback] User has company_id, starting import...');
                                    setLoginPrompt(false);
                                    setImporting(true);
                                    setImportProgress('Starting Shopify import...');
                                    
                                    await importShopifyTransactions({
                                        companyId: effectiveCompanyId,
                                        integrationId: integrationContext?.integrationId,
                                        setImporting,
                                        setUploadError: setError,
                                        onProgress: setImportProgress,
                                        onComplete: (success) => {
                                            setImporting(false);
                                            if (success) {
                                                enqueueSnackbar('Shopify import complete!', { variant: 'success' });
                                                setTimeout(() => navigate('/platforms/integrations'), 2000);
                                            } else {
                                                setError('Shopify import failed.');
                                            }
                                        }
                                    });
                                } else {
                                    logger.log('[Shopify Callback] Still no user or company_id after manual refresh');
                                    setError('Unable to restore session. Please log in again and try the Shopify import from the integrations page.');
                                    setRestoring(false);
                                }
                            } catch (refreshError) {
                                logger.error('[Shopify Callback] Manual session refresh error:', refreshError);
                                setError('Session restore failed. Please log in again and try the Shopify import from the integrations page.');
                                setRestoring(false);
                            }
                        }}
                        disabled={restoring}
                        style={{ padding: '8px 16px', fontSize: '1rem', borderRadius: 4, background: '#1976d2', color: '#fff', border: 'none', cursor: 'pointer' }}
                    >
                        {restoring ? 'Restoring...' : 'Restore Session'}
                    </button>
                </Box>
                <Typography
                    variant="body2"
                    color="primary"
                    sx={{ cursor: 'pointer', mb: 2 }}
                    onClick={() => navigate('/platforms/integrations')}
                >
                    Go to Integrations
                </Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                minHeight="50vh"
                p={3}
            >
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    Please try connecting to Shopify again.
                </Typography>
                {syncResult && syncResult.error && (
                    <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                        Sync error: {syncResult.error}
                    </Typography>
                )}
                <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column', alignItems: 'center' }}>
                    <Typography
                        variant="body2"
                        color="primary"
                        sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                        onClick={() => navigate('/platforms/integrations')}
                    >
                        Return to Integrations
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        You will be automatically redirected in 5 seconds...
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                        Current URL: {window.location.href}
                    </Typography>
                </Box>
            </Box>
        );
    }

    return (
        <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            minHeight="50vh"
            p={3}
        >
            <CircularProgress sx={{ mb: 2 }} />
            <Typography variant="h6" sx={{ mb: 1 }}>
                {importing ? 'Importing from Shopify...' : 'Connecting to Shopify...'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
                {importing ? importProgress : 'Please wait while we complete your Shopify connection.'}
            </Typography>
        </Box>
    );
};

export default ShopifyCallback; 